import { baseActions, baseMutations, baseState, BaseState } from "@/store/modules/helpers/baseState";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Leverantor = {
    id: number;
    namn: string;
};

type State = BaseState & {
    leverantorer: Map<Leverantor["id"], Leverantor>;
};

export type BaseActionsProps<T> = {
    state: BaseState & T;
    commit: <K>(
        event: "reset" | string,
        payload?: K,
        options?: {
            root?: boolean;
        }
    ) => void;
};

export type FilterParams = Partial<{
    ordering: string;
}> & {
    [key: string]: string | number | boolean;
};

const getDefaultState: () => State = () => {
    return {
        leverantorer: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "leverantor";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        leverantorer: (state: State) => [...state.leverantorer.values()],
        leverantorById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.leverantorer.get(id);
            },
        leverantorerSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({
                ...props,
                state,
                collectionName: "leverantorer",
            }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addLeverantorer: (
            state: State,
            {
                offset,
                subsetName = "default",
                leverantorer = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                leverantorer: Leverantor[];
            }
        ) =>
            subsetSetter({
                offset,
                subsetName,
                state,
                collectionName: "leverantorer",
                items: leverantorer,
            }),
        updateLeverantor: (
            state: State,
            {
                leverantor,
                offset,
                subsetName,
            }: {
                leverantor: Leverantor[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Leverantor>({
                offset,
                state,
                collectionName: "leverantorer",
                items: leverantor,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        /**
         * Hämta material från state eller från backend och stoppa in i state. Den returnerar inte resultatet.
         * Det du troligen vill göra är att skapa en computed som lyssnar på subsetgettern.
         * Den skapar ett nytt subset om inskickade filteringParams inte redan finns i state.
         * @param limit mängden objekt du vill få tillbaka i resultatet
         * @param page nummer för page. Används för att räkna ut offset som skickas till BE
         * @param filterParams alla sök och filtreringsparametrar. "ordering" är för sortering
         */
        fetchLeverantorer: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams,
            }: {
                limit: number;
                page: number;
                filterParams: FilterParams;
            }
        ) => {
            try {
                const { data } = await klinikenApi.get("operation/leverantorer/", getConfig());
                const results = data.results;

                const subsetName = "default";
                const total = 0;
                const nextPage = 0;
                const offset = 0;

                if (!results) {
                    return {
                        // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                        subsetName, // Totala mängden träffar från sökningen
                        total, // Nummer för nästa page
                        nextPage,
                    };
                }

                commit("addLeverantorer", {
                    offset,
                    leverantorer: results,
                });

                return {
                    // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                    subsetName, // Totala mängden träffar från sökningen
                    total, // Nummer för nästa page
                    nextPage,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createLeverantor: async (
            { commit, state }: BaseActionsProps<State>,
            {
                leverantor,
            }: {
                leverantor: Leverantor;
            }
        ) => {
            try {
                const response = await klinikenApi.post("operation/leverantorer/", leverantor, getConfig({}));

                commit("addLeverantorer", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    leverantorer: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateLeverantor: async (
            { commit, state }: BaseActionsProps<State>,
            {
                leverantor,
            }: {
                leverantor: Leverantor;
            }
        ) => {
            try {
                const response = await klinikenApi.put(
                    `operation/leverantorer/${leverantor.id}/`,
                    leverantor,
                    getConfig({})
                );

                commit("updateLeverantor", {
                    subsetName: "newlyUpdated",
                    leverantor: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
